import { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import AdminLayout from '@/components/layout/AdminLayout'
import DashboardLayout from '@/components/layout/DashboardLayout'
import ProtectedLayout from '@/components/layout/ProtectedLayout'
import SuperadminGuard from '@/components/layout/SuperadminGuard'
import TeacherLayout from '@/components/layout/TeacherLayout'
// Layouts
import TenantLayout from '@/components/layout/TenantLayout'
import { useAuth } from '@/lib/auth'
import { useBackendHealth } from '@/lib/useBackendHealth'
import AdminCourseEnrollmentsPage from '@/pages/admin/AdminCourseEnrollmentsPage'
import AdminEnrollmentsPage from '@/pages/admin/AdminEnrollmentsPage'
import AdminRatingsPage from '@/pages/admin/AdminRatingsPage'
import AdminTenantsPage from '@/pages/admin/AdminTenantsPage'
import AdminUserEnrollmentsPage from '@/pages/admin/AdminUserEnrollmentsPage'
// Admin Pages
import AdminUsersPage from '@/pages/admin/AdminUsersPage'
import TenantFormPage from '@/pages/admin/TenantFormPage'
import AuthCallbackPage from '@/pages/auth/AuthCallbackPage'
// Auth Pages
import LoginPage from '@/pages/auth/LoginPage'
import SelectTenantPage from '@/pages/auth/SelectTenantPage'
import TenantLoginPage from '@/pages/auth/TenantLoginPage'
// Student Pages
import CertificatesPage from '@/pages/student/CertificatesPage'
import CourseDetailPage from '@/pages/student/CourseDetailPage'
import CoursesPage from '@/pages/student/CoursesPage'
import DashboardPage from '@/pages/student/DashboardPage'
import LearnPage from '@/pages/student/LearnPage'
import AttendanceManagePage from '@/pages/teacher/AttendanceManagePage'
import CourseFormPage from '@/pages/teacher/CourseFormPage'
import LessonFormPage from '@/pages/teacher/LessonFormPage'
import NotificationTemplatesPage from '@/pages/teacher/NotificationTemplatesPage'
import OrganizationImportPage from '@/pages/teacher/OrganizationImportPage'
import TagsPage from '@/pages/teacher/TagsPage'
import TeacherCoursesPage from '@/pages/teacher/TeacherCoursesPage'
// Teacher Pages
import TeacherDashboardPage from '@/pages/teacher/TeacherDashboardPage'
import TeacherLessonAnalyticsPage from '@/pages/teacher/TeacherLessonAnalyticsPage'
import TeacherLessonsPage from '@/pages/teacher/TeacherLessonsPage'
import TeacherManualPage from '@/pages/teacher/TeacherManualPage'
import TeacherSettingsPage from '@/pages/teacher/TeacherSettingsPage'
import TeacherStudentsPage from '@/pages/teacher/TeacherStudentsPage'
import TenantTrainingConfigPage from '@/pages/teacher/TenantTrainingConfigPage'
import TrainingAssignPage from '@/pages/teacher/TrainingAssignPage'
import TrainingFormPage from '@/pages/teacher/TrainingFormPage'
import TrainingManagePage from '@/pages/teacher/TrainingManagePage'
import TrainingReportPage from '@/pages/teacher/TrainingReportPage'
import TrainingSkillItemsPage from '@/pages/teacher/TrainingSkillItemsPage'
import TrainingSurveyManagePage from '@/pages/teacher/TrainingSurveyManagePage'
// Training Pages (受講管理)
import ApprovalListPage from '@/pages/training/ApprovalListPage'
import FollowUpPage from '@/pages/training/FollowUpPage'
import MyEnrollmentsPage from '@/pages/training/MyEnrollmentsPage'
import SubordinateEnrollmentsPage from '@/pages/training/SubordinateEnrollmentsPage'
import TrainingDetailPage from '@/pages/training/TrainingDetailPage'
import TrainingListPage from '@/pages/training/TrainingListPage'

function StartingUpScreen() {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [])
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <p className="text-lg font-medium">サーバーを起動中です...</p>
      <p className="text-sm text-muted-foreground">
        {elapsed < 60
          ? `${elapsed} 秒経過 — 自動的に再接続します`
          : `${Math.floor(elapsed / 60)} 分 ${elapsed % 60} 秒経過 — もうしばらくお待ちください`}
      </p>
    </div>
  )
}

function TenantSlugLoginRedirect() {
  const { slug } = useParams<{ slug: string }>()
  return <Navigate to={`/${slug}/login`} replace />
}

function RootRedirect() {
  const { user, isLoading, isAuthenticated, backendUnavailable, retryAuth } = useAuth()

  useBackendHealth(backendUnavailable, retryAuth)

  if (isLoading) return <div className="flex h-screen items-center justify-center">Loading...</div>

  if (backendUnavailable) return <StartingUpScreen />

  if (!isAuthenticated) return <Navigate to="/login" replace />

  // Superadmin without tenant → tenant management
  if (user?.role === 'superadmin' && !user.tenantSlug) {
    return <Navigate to="/admin/tenants" replace />
  }

  // User with tenant → tenant dashboard
  if (user?.tenantSlug) {
    return <Navigate to={`/${user.tenantSlug}/dashboard`} replace />
  }

  // Multiple tenants → select
  return <Navigate to="/select-tenant" replace />
}

export default function App() {
  return (
    <Routes>
      {/* Global routes (no tenant) */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/select-tenant" element={<SelectTenantPage />} />

      {/* Superadmin tenant management (no tenant context, auth required) */}
      <Route path="/admin" element={<SuperadminGuard />}>
        <Route path="tenants" element={<AdminTenantsPage />} />
        <Route path="tenants/new" element={<TenantFormPage />} />
        <Route path="tenants/:id/edit" element={<TenantFormPage />} />
      </Route>

      {/* 旧URL互換: /tenant/:slug/login → /:slug/login */}
      <Route path="/tenant/:slug/login" element={<TenantSlugLoginRedirect />} />

      {/* Tenant routes */}
      <Route path="/:tenantSlug" element={<TenantLayout />}>
        {/* Public: tenant login */}
        <Route path="login" element={<TenantLoginPage />} />

        {/* Protected routes */}
        <Route element={<ProtectedLayout />}>
          {/* Student (dashboard) */}
          <Route element={<DashboardLayout />}>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="certificates" element={<CertificatesPage />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="courses/:id" element={<CourseDetailPage />} />
            <Route path="learn/:courseId" element={<LearnPage />} />

            {/* 受講管理（研修） */}
            <Route path="trainings" element={<TrainingListPage />} />
            <Route path="trainings/:id" element={<TrainingDetailPage />} />
            <Route path="my-enrollments" element={<MyEnrollmentsPage />} />
            <Route path="follow-ups/:enrollmentId" element={<FollowUpPage />} />
            <Route path="approvals" element={<ApprovalListPage />} />
            <Route path="subordinates" element={<SubordinateEnrollmentsPage />} />
          </Route>

          {/* Teacher */}
          <Route path="teacher" element={<TeacherLayout />}>
            <Route index element={<TeacherDashboardPage />} />
            <Route path="courses" element={<TeacherCoursesPage />} />
            <Route path="courses/new" element={<CourseFormPage />} />
            <Route path="courses/:id/edit" element={<CourseFormPage />} />
            <Route path="courses/:courseId/lessons/new" element={<LessonFormPage />} />
            <Route path="courses/:courseId/lessons/:id/edit" element={<LessonFormPage />} />
            <Route path="lessons" element={<TeacherLessonsPage />} />
            <Route path="lessons/new" element={<LessonFormPage />} />
            <Route path="lessons/:id/edit" element={<LessonFormPage />} />
            <Route path="lessons/:id/analytics" element={<TeacherLessonAnalyticsPage />} />
            <Route path="tags" element={<TagsPage />} />
            <Route path="students" element={<TeacherStudentsPage />} />

            {/* 受講管理（研修） */}
            <Route path="trainings" element={<TrainingManagePage />} />
            <Route path="trainings/new" element={<TrainingFormPage />} />
            <Route path="trainings/:id/edit" element={<TrainingFormPage />} />
            <Route path="trainings/:id/assign" element={<TrainingAssignPage />} />
            <Route path="trainings/:id/attendance" element={<AttendanceManagePage />} />
            <Route path="trainings/:id/skill-items" element={<TrainingSkillItemsPage />} />
            <Route path="training-reports/:trainingId" element={<TrainingReportPage />} />
            <Route path="training-surveys" element={<TrainingSurveyManagePage />} />
            <Route path="notification-templates" element={<NotificationTemplatesPage />} />
            <Route path="training-config" element={<TenantTrainingConfigPage />} />
            <Route path="organization" element={<OrganizationImportPage />} />
            <Route path="settings" element={<TeacherSettingsPage />} />
            <Route path="manual" element={<TeacherManualPage />} />
          </Route>

          {/* Admin */}
          <Route path="admin" element={<AdminLayout />}>
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="enrollments" element={<AdminEnrollmentsPage />} />
            <Route path="enrollments/user/:userId" element={<AdminUserEnrollmentsPage />} />
            <Route path="enrollments/course/:courseId" element={<AdminCourseEnrollmentsPage />} />
            <Route path="ratings" element={<AdminRatingsPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  )
}
